import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Tag, ArrowLeft } from 'lucide-react';

const NewDistributionPartnership = () => {
  return (
    <>
      <Helmet>
        <title>New Book Distribution Partnership | Rushmore Publishing</title>
        <meta name="description" content="Rushmore Publishing announces new partnership with major distributors to expand global reach for authors." />
      </Helmet>

      <article className="min-h-screen bg-white pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/news-updates" className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 mb-8 font-semibold">
            <ArrowLeft className="w-5 h-5" /> Back to News
          </Link>

          <div className="mb-8">
            <span className="bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Company News
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Rushmore Publishing Announces New Book Distribution Partnership
          </h1>

          <div className="flex items-center gap-2 text-gray-600 mb-8 pb-8 border-b border-gray-200">
            <Calendar className="w-5 h-5" />
            <span>January 18, 2025</span>
          </div>

          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Distribution Partnership"
            className="w-full h-96 object-cover rounded-2xl mb-12"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Rushmore Publishing is thrilled to announce a groundbreaking partnership with leading book distributors that will significantly expand the global reach of our authors' works.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Expanding Global Reach</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              This strategic partnership will enable our authors to reach readers in over 75 countries across six continents. The new distribution channels include major retail chains, independent bookstores, libraries, and digital platforms worldwide.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              "This partnership represents a major milestone in our mission to connect talented authors with readers globally," said our CEO. "We're committed to ensuring our authors' books are available wherever readers want to discover them."
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Key Partnership Benefits</h2>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Wider Distribution:</strong> Access to 10,000+ bookstores and retail locations worldwide</li>
              <li><strong>Faster Delivery:</strong> Improved logistics ensure books reach readers in 3-5 business days</li>
              <li><strong>Digital Expansion:</strong> Enhanced presence on major e-book platforms and audiobook services</li>
              <li><strong>Library Access:</strong> Direct distribution to university and public libraries across North America and Europe</li>
              <li><strong>International Markets:</strong> Dedicated support for translations and international editions</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What This Means for Authors</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Authors publishing with Rushmore will automatically benefit from this expanded distribution network. No additional fees or complex processes - your books will simply be available to more readers in more places.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              This includes both print and digital formats, with seamless coordination across all channels. Authors will receive detailed sales reports showing distribution across all regions and formats.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Enhanced Marketing Support</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Alongside expanded distribution, we're launching new marketing initiatives to promote books through our distribution partners. This includes:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Featured placement opportunities in major retail locations</li>
              <li>Coordinated launch campaigns across multiple markets</li>
              <li>Enhanced discoverability through distributor databases and catalogs</li>
              <li>Partnership with book subscription services and reading clubs</li>
            </ul>

            <div className="bg-amber-50 border-l-4 border-amber-700 p-6 my-12 rounded-r-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Partnership Timeline</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>January 2025:</strong> Partnership launch and initial distribution expansion</li>
                <li><strong>February 2025:</strong> Full catalog integration across all partner channels</li>
                <li><strong>March 2025:</strong> International distribution goes live in Asia-Pacific region</li>
                <li><strong>April 2025:</strong> Launch of enhanced marketing programs</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Looking Ahead</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              This partnership is just the beginning. We're actively working on additional distribution agreements and exploring new channels to ensure our authors have every advantage in today's competitive publishing landscape.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Our commitment remains unwavering: to provide authors with the best possible platform to share their stories with the world. This partnership brings us closer to that goal.
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-gray-600 mb-6">Ready to publish with Rushmore?</p>
            <Link to="/" className="inline-block">
              <button className="blob-btn">
                Get Started Today
                <span className="blob-btn__inner">
                  <span className="blob-btn__blobs">
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                  </span>
                </span>
              </button>
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default NewDistributionPartnership;